import { useRecoilValue } from "recoil";
import { SecretNetworkClient } from "secretjs";

import { useToken } from "./useToken";
import { useConfig } from "./useConfig";
import { defaultGas, sleepTime } from "../utils/constants";
import { useIbcTxnClient } from "./useIbcTxnClient";
import { coinConvert, sleep } from "../utils/common";
import { walletState } from "../context/walletState";
import { useIbcQueryClient } from "./useIbcQueryClient";
import { queryClientState } from "../context/queryClientState";
import { SecretswapGeneratorQueryContract, SecretswapGeneratorContract } from "./clients/SecretswapGeneratorContract";
import { toast } from "react-toastify";
import { useMessageToaster } from "./useMessageToaster";

export const useIBC = () => {
  const { address, client } = useRecoilValue(walletState);
  const { queryClient } = useRecoilValue(queryClientState);
  const token = useToken();
  const config = useConfig();
  const toaster = useMessageToaster();

  const generatorQueryClient = new SecretswapGeneratorQueryContract(
    queryClient as SecretNetworkClient,
    config.getOtherContract("generator").contract_addr as string,
    config.getOtherContract("generator").contract_code_hash as string
  );
  const generatorClient = new SecretswapGeneratorContract(
    client as SecretNetworkClient,
    config.getOtherContract("generator").contract_addr as string,
    config.getOtherContract("generator").contract_code_hash as string
  );

  const ibcTxn = useIbcTxnClient();

  const checkQueryClient = async () => {
    // while (true) {
    //   if (queryClient === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  const checkTxnClient = async () => {
    // while (true) {
    //   if (client === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  const checkAddressClient = async () => {
    // while (true) {
    //   if (address === undefined) {
    //     await sleep(sleepTime);
    //   } else {
    //     break;
    //   }
    // }
  }

  /*
   * Returns the balance of native/snip20 token on Secret Network 
   */
  const getTokenScrtBalance = async (tokenName: string) => {
    try {
      const tokenBalance = await token.getBalance(tokenName);
      return tokenBalance;
    } catch (error) {
      console.debug(error);
    }
  };

  /*
   * Returns the balance of native/snip20 token on Source Network (Juno, Osmosis etc) 
   */
  const getTokenSourceBalance = async (tokenName: string) => {
    const ibcTxnClient = await ibcTxn.getTokenClient(tokenName);

    try {
      const balanceResponse = await (
        ibcTxnClient?.wasmChainClient as SecretNetworkClient
      ).query.bank.balance({
        address: ibcTxnClient?.address as string,
        denom: config.getAsset(tokenName).ibc?.denom as string,
      });

      return coinConvert(balanceResponse?.balance?.amount as string, 6, "human");
    } catch (error) {
      console.debug(error);
    }
  }

  /*
   * Sends the token on Secret Network to Source Network
   */
  const getTokenSourceAddressShort = async(tokenName: string) => {
    try {
      const ibcTxnClient = await ibcTxn.getTokenClient(tokenName);
      const addrss = ibcTxnClient?.address;
      const ibcshortaddrss = addrss.substr(0, 13) + "..." + addrss.substr(addrss.length - 8, 8);
      return ibcshortaddrss;
    } catch (error) {
      console.debug(error);
    }
  }

  /*
   * Returns the claimable rewards for the user for all pools
   */
  const getPendingRewards = async () => {
    // call pendingToken for all lpTokens in pools_list
    await checkQueryClient();
    await checkAddressClient();

    try {
      let totalPending = 0;
      const poolsList = config.getPoolsList();
      for(const pool of poolsList) {
        const pendingPool = await generatorQueryClient.pendingToken({
          lpToken: config.getPoolsLPAsset(pool.pair_name).lpAddress as string,
          user: address as string,
        });
        
        totalPending += parseInt(pendingPool.pending);
      }

      return coinConvert(totalPending.toString() as string, 6, "human");
    } catch (error) {
      console.debug(error);
    }
  }

  /*
   * Sends the token on Secret Network to Source Network
   */
  const doToSourceTransfer = async (
    tokenName: string,
    tokenAmount: string,
    destAddress?: string | undefined,
    gasValue?: string | undefined,
  ) => {
    const asset = config.getAsset(tokenName);
    const ibcTxnClient = await ibcTxn.getTokenClient(tokenName);

    await checkTxnClient();

    try {
      const transferResponse = await (
        client as SecretNetworkClient
      ).tx.ibc.transfer(
        {
          sender: address as string,
          receiver: (destAddress !== undefined)? destAddress: ibcTxnClient?.address,
          source_channel: asset.ibc?.scrt_channel as string,
          source_port: "transfer",
          token: {
            amount: coinConvert(tokenAmount, 6, "machine"),
            denom: asset.ibc?.scrt_denom as string,
          },
          timeout_timestamp: String(
            Math.floor(Date.now() / 1000) + 10 * 60
          ), // 10 minute timeout
        },
        {
          gasLimit: parseInt((gasValue !== undefined)? gasValue: defaultGas.ibc_withdraw),
          ibcTxsOptions: {
            resolveResponsesCheckIntervalMs: 10_000,
            resolveResponsesTimeoutMs: 10.25 * 60 * 1000,
          },
        },
      );

      return transferResponse;
    } catch (error) {
      console.debug(error);
    }
  };

  /*
   * Sends the token on Source Network to Secret Network
   */
  const doToScrtTransfer = async (
    tokenName: string,
    tokenAmount: string,
    destAddress?: string | undefined,
    gasValue?: string | undefined,
  ) => {
    const asset = config.getAsset(tokenName);
    const ibcTxnClient = await ibcTxn.getTokenClient(tokenName);

    await checkAddressClient();

    try {
      const transferResponse = await (
        ibcTxnClient?.wasmChainClient as SecretNetworkClient
      ).tx.ibc.transfer(
        {
          sender: ibcTxnClient?.address as string,
          receiver: (destAddress !== undefined)? destAddress: address as string,
          source_channel: asset.ibc?.src_channel as string,
          source_port: "transfer",
          token: {
            amount: coinConvert(tokenAmount, 6, "machine"),
            denom: asset.ibc?.denom as string,
          },
          timeout_timestamp: String(
            Math.floor(Date.now() / 1000) + 10 * 60
          ), // 10 minute timeout
        },
        {
          gasLimit: parseInt((gasValue !== undefined)? gasValue: defaultGas.ibc_deposit),
          ibcTxsOptions: {
            resolveResponsesCheckIntervalMs: 10_000,
            resolveResponsesTimeoutMs: 10.25 * 60 * 1000,
          },
        },
      );

      return transferResponse;
    } catch (error) {
      console.debug(error);
    }
  };

  /*
   * Claims all the pending rewards for user
   */
  const doClaimRewards = async (
    gasValue?: string | undefined,
  ) => {
    await checkTxnClient();
    const tid = "Request Rejected";
    
    const lpTokens = config.getPoolsList().map((pool): [string, string] => {
      const lpToken = config.getPoolsLPAsset(pool.pair_name);
      return [
        lpToken.lpAddress,
        lpToken.lpCodeHash,
      ]
    });

    try {
      const claimResponse = await generatorClient.claimRewards(
        {
          userAddress: address as string,
          customFees: {
            gas: (gasValue !== undefined)? gasValue: defaultGas.claim_rewards,
            amount: [],
          }
        },
        {
          lpTokens: lpTokens,
        }
      );

      if(claimResponse.code || claimResponse===undefined){
        toaster.Error("Failed to claim rewards.");
        toaster.Error((claimResponse.rawLog).substr(0,100) + "...");
      }
    else toast.success(`Rewards claimed successfully`,{
      type: "success",
    });

      return claimResponse;
    } catch (error) {
      console.debug(error);
      toaster.Error("Failed to claim rewards.");
      toast.info(tid, {
        type: "error",
        closeOnClick: true,
        // render: "Request rejected",
        autoClose: 5000,
        isLoading: false,
      });
      if (error instanceof Error) {
        toaster.Error(error.message);
      }
    }
  }

  return {
    getTokenScrtBalance,
    getTokenSourceBalance,
    getTokenSourceAddressShort,
    getPendingRewards,
    doToSourceTransfer,
    doToScrtTransfer,
    doClaimRewards,
  };
};
